/**
 * Shared checkbox email template editor (Missing Info, Denied Reason).
 * Master–detail layout for `missingInfoAngular` and `deniedAngular` variants.
 */

import { useState, useEffect, useRef } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import type {
  SubmitErrorHandler,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { Resolver } from 'react-hook-form'
import {
  missingInfoFormSchema,
  deniedInfoFormSchema,
  type MissingInfoFormValues,
} from '../types/schemas'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import type {
  EmailCheckboxTemplateFormProps,
  EmailCheckboxTemplateSectionProps,
} from '../types'
import { trackApplicationEvent } from '@lib/utils/analytics'
import { showError } from '@/lib/utils/notifications'
import {
  UNDERWRITING_DENIED_FORM_ID,
  UNDERWRITING_MISSING_INFO_FORM_ID,
} from '../constants/formIds'
import { CheckboxTemplateMasterDetail } from './CheckboxTemplateMasterDetail'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { firstNestedErrorMessage } from '../utils/formErrors'
import { normalizeTemplateKey } from '../utils/templateKeys'
import { nextSelectedIndexAfterRemoveSection, indexForNewSection } from '../utils/selectionUtils'

export function EmailCheckboxTemplateForm({
  template,
  isLoading,
  onSave,
  onSuccess,
  onError,
  sendTemplateEditAnalytics = true,
  variant = 'default',
  title = '',
  description = '',
}: EmailCheckboxTemplateFormProps) {
  const [submitLoading, setSubmitLoading] = useState(false)
  const masterDetail =
    variant === 'missingInfoAngular' || variant === 'deniedAngular'
  const deniedLike = variant === 'deniedAngular'
  const [sectionModalIndex, setSectionModalIndex] = useState<number | null>(null)
  /** Stable row id for last “Add New Section” append — removed on Cancel if still that row. */
  const pendingAppendFieldIdRef = useRef<string | null>(null)
  const capturePendingAppendIdRef = useRef(false)

  const resolverSchema = deniedLike ? deniedInfoFormSchema : missingInfoFormSchema

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<MissingInfoFormValues>({
    resolver: zodResolver(resolverSchema) as Resolver<MissingInfoFormValues>,
    mode: 'onChange',
    defaultValues: { checkbox_data: [] },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'checkbox_data' })

  useEffect(() => {
    if (!template) return
    const isCheckboxData = String(template.is_checkbox_data) === '1'
    if (isCheckboxData && Array.isArray(template.checkbox_data)) {
      reset({
        checkbox_data: template.checkbox_data.map((section) => ({
          key: normalizeTemplateKey(section.key),
          label: section.label ?? '',
          description: section.description ?? '',
          hasInput: Boolean(section.hasInput),
          child: (section.child ?? []).map((child) => ({
            key: normalizeTemplateKey(child.key),
            label: child.label ?? '',
            description: child.description ?? '',
            hasInput: Boolean(child.hasInput),
            isLock: Number(child.isLock) === 1 ? 1 : 0,
          })),
        })),
      })
    } else {
      reset({
        checkbox_data: [],
      })
    }
  }, [template, reset])

  useEffect(() => {
    if (!masterDetail) return
    if (fields.length === 0) {
      setSectionModalIndex(null)
      return
    }
    setSectionModalIndex((prev) => {
      if (prev === null) return null
      if (prev >= fields.length) return fields.length - 1
      return prev
    })
  }, [fields.length, masterDetail])

  useEffect(() => {
    if (!masterDetail || !capturePendingAppendIdRef.current || sectionModalIndex === null) return
    const row = fields[sectionModalIndex]
    if (row) {
      pendingAppendFieldIdRef.current = row.id
      capturePendingAppendIdRef.current = false
    }
  }, [masterDetail, fields, sectionModalIndex])

  const handleDismissSectionModal = () => {
    const pid = pendingAppendFieldIdRef.current
    if (pid !== null && sectionModalIndex !== null) {
      const at = fields[sectionModalIndex]
      if (at?.id === pid) {
        const removeIndex = fields.findIndex((f) => f.id === pid)
        if (removeIndex !== -1) {
          remove(removeIndex)
        }
      }
    }
    pendingAppendFieldIdRef.current = null
    capturePendingAppendIdRef.current = false
    setSectionModalIndex(null)
  }

  const onSubmit = async (data: MissingInfoFormValues) => {
    try {
      setSubmitLoading(true)
      await onSave(data)
      if (sendTemplateEditAnalytics) {
        trackApplicationEvent('email_template_saved', 'email')
      }
      pendingAppendFieldIdRef.current = null
      capturePendingAppendIdRef.current = false
      if (masterDetail) {
        setSectionModalIndex(null)
      }
      reset(data)
      onSuccess()
    } catch (error) {
      onError(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  const onSubmitInvalid: SubmitErrorHandler<MissingInfoFormValues> = (errs) => {
    const msg =
      firstNestedErrorMessage(errs) ??
      'Fix validation errors before submitting (required keys cannot be empty).'
    showError(msg)
  }

  const onAddSection = () => {
    capturePendingAppendIdRef.current = true
    const idx = indexForNewSection(fields.length)
    append({ key: '', label: '', description: '', hasInput: false, child: [] })
    setSectionModalIndex(idx)
  }

  const onRemoveSection = (index: number) => {
    const removed = fields[index]
    if (removed && removed.id === pendingAppendFieldIdRef.current) {
      pendingAppendFieldIdRef.current = null
      capturePendingAppendIdRef.current = false
    }
    const nextLen = fields.length - 1
    remove(index)
    setSectionModalIndex((prev) =>
      nextSelectedIndexAfterRemoveSection(index, prev, nextLen),
    )
  }

  if (isLoading) {
    return <ModuleScreenLoading minHeight="300px" />
  }

  // Default variant: do not gate on isDirty (CKEditor often fails to mark dirty).
  const submitDisabled = submitLoading

  /** Master–detail tabs (Missing Info, Denied): Submit / Submitting…; legacy default variant: Save / Saving… */
  const submitLabel = submitLoading
    ? masterDetail
      ? 'Submitting...'
      : 'Saving...'
    : masterDetail
      ? 'Submit'
      : 'Save'

  /** List footer: only disabled while a save is in flight (not gated on isDirty — CKEditor often fails to mark dirty). */
  const masterDetailFooterSubmitDisabled = submitLoading

  if (masterDetail) {
    const masterDetailFormId = deniedLike
      ? UNDERWRITING_DENIED_FORM_ID
      : UNDERWRITING_MISSING_INFO_FORM_ID

    return (
      <form
        id={masterDetailFormId}
        onSubmit={handleSubmit(onSubmit, onSubmitInvalid)}
        className="relative space-y-6 pb-28"
      >
        <CheckboxTemplateMasterDetail
          mode={deniedLike ? 'denied' : 'missingInfo'}
          control={control}
          register={register}
          errors={errors}
          sectionFields={fields}
          onAddSection={onAddSection}
          onRemoveSection={onRemoveSection}
          sectionModalIndex={sectionModalIndex}
          setSectionModalIndex={setSectionModalIndex}
          onDismissSectionModal={handleDismissSectionModal}
          parentFormId={masterDetailFormId}
          submitLabel={submitLabel}
          submitLoading={submitLoading}
        />

        <div
          className="sticky bottom-0 z-20 -mx-2 mt-4 flex justify-end gap-2 border-t border-slate-200 bg-white/95 px-3 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.06)] backdrop-blur supports-[backdrop-filter]:bg-white/90"
          role="toolbar"
          aria-label="Form actions"
        >
          <button
            type="submit"
            disabled={masterDetailFooterSubmitDisabled}
            className={primaryEntityActionButtonClass}
          >
            {submitLabel}
          </button>
        </div>
      </form>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit, onSubmitInvalid)} className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
        <p className="text-sm text-slate-600">{description}</p>
      </div>

      <div className="space-y-4">
        {fields.length === 0 && (
          <p className="text-sm text-slate-500 py-4">No sections added yet.</p>
        )}
        {fields.map((field, sectionIndex) => (
          <TemplateSection
            key={field.id}
            fieldName={`checkbox_data.${sectionIndex}`}
            sectionIndex={sectionIndex}
            register={register}
            control={control}
            errors={errors}
            onRemove={() => remove(sectionIndex)}
          />
        ))}
      </div>

      <div className="flex gap-2 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={() => append({ key: '', label: '', description: '', hasInput: false, child: [] })}
          className={secondaryEntityActionButtonClass}
        >
          Add Section
        </button>
        <button
          type="submit"
          disabled={submitDisabled}
          className={primaryEntityActionButtonClass}
        >
          {submitLabel}
        </button>
      </div>
    </form>
  )
}

function TemplateSection({
  fieldName,
  sectionIndex,
  register,
  control,
  errors,
  onRemove,
}: EmailCheckboxTemplateSectionProps) {
  const {
    fields: childFields,
    append: appendChild,
    remove: removeChild,
  } = useFieldArray({ control, name: `${fieldName}.child` })

  const sectionErrors = errors.checkbox_data?.[sectionIndex]

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-4 bg-slate-50">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-medium text-slate-900">Section {sectionIndex + 1}</h4>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-600 hover:text-red-700 text-sm font-medium"
        >
          Remove
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Key</label>
        <input
          type="text"
          placeholder="Section key"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-button/30"
          {...register(`${fieldName}.key`)}
        />
        {sectionErrors?.key && (
          <p className="text-xs text-red-600 mt-1">{sectionErrors.key.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Label (HTML)</label>
        <Controller
          name={`${fieldName}.label`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <CKEditor4 id={`${fieldName}.label`} value={value || ''} onChange={onChange} />
          )}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-slate-700">Items</label>
          <button
            type="button"
            onClick={() =>
              appendChild({ key: '', label: '', description: '', hasInput: false })
            }
            className="text-sm font-medium text-button hover:text-button-hover"
          >
            + Add Item
          </button>
        </div>

        <div className="space-y-3">
          {childFields.map((childField, childIndex) => {
            const childErrors = sectionErrors?.child?.[childIndex]
            return (
              <div
                key={childField.id}
                className="border border-slate-200 rounded p-3 bg-white space-y-2"
              >
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Key</label>
                    <input
                      type="text"
                      placeholder="Item key"
                      className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-button/30"
                      {...register(`${fieldName}.child.${childIndex}.key`)}
                    />
                    {childErrors?.key && (
                      <p className="text-xs text-red-600 mt-1">{childErrors.key.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="flex items-center text-xs font-medium text-slate-600 h-full pt-3">
                      <input
                        type="checkbox"
                        className="mr-2 h-4 w-4 rounded accent-button"
                        {...register(`${fieldName}.child.${childIndex}.hasInput`)}
                      />
                      Has Input
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Label (HTML)
                  </label>
                  <Controller
                    name={`${fieldName}.child.${childIndex}.label`}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <CKEditor4
                        id={`${fieldName}.child.${childIndex}.label`}
                        value={value || ''}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Description (HTML)
                  </label>
                  <Controller
                    name={`${fieldName}.child.${childIndex}.description`}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <CKEditor4
                        id={`${fieldName}.child.${childIndex}.description`}
                        value={value || ''}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => removeChild(childIndex)}
                  className="text-red-600 hover:text-red-700 text-xs font-medium"
                >
                  Remove Item
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
